import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import PanToolIcon from '@mui/icons-material/PanTool';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

export type PlayerRole = 'civilian' | 'mafia' | 'don' | 'sheriff' | 'unknown';

export interface PlayerState {
  id: number;
  role: PlayerRole;
  isAlive: boolean;
  isCheckedBySheriff: boolean;
  votesFor: number[];
}

interface PlayerCircleProps {
  player: PlayerState;
  onClick: (id: number) => void;
  onRightClick: (id: number) => void;
  size?: number;
}

// Role-based styling
const getRoleColor = (role: PlayerRole) => {
  switch (role) {
    case 'civilian':
      return '#e57373'; // Light Red
    case 'sheriff':
      return '#c62828'; // Dark Red
    case 'mafia':
      return '#424242'; // Dark Gray
    case 'don':
      return '#212121'; // Almost Black
    case 'unknown':
      return '#c8b3c4'; // Gray pinkish
    default:
      return '#bdbdbd'; // Grey
  }
};

const getRoleIcon = (role: PlayerRole) => {
  switch (role) {
    case 'sheriff':
      return <LocalPoliceIcon fontSize="small" />;
    case 'don':
      return <PanToolIcon fontSize="small" />; // Hand icon representing power/control
    case 'mafia':
      return <FiberManualRecordIcon fontSize="small" style={{ fontSize: '14px' }} />;
    case 'unknown':
      return <HelpOutlineIcon fontSize="small" style={{ fontSize: '16px' }} />;
    default:
      return null;
  }
};

const PlayerContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isAlive' && prop !== 'roleColor'
})<{ isAlive: boolean; roleColor: string }>(({ isAlive, roleColor }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  backgroundColor: roleColor,
  opacity: isAlive ? 1 : 0.4,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.4)'
  }
}));

const PlayerCircle: React.FC<PlayerCircleProps> = ({ 
  player, 
  onClick, 
  onRightClick,
  size = 60 
}) => {
  const handleClick = () => {
    onClick(player.id);
  };

  const handleRightClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onRightClick(player.id);
  };

  const roleColor = getRoleColor(player.role);
  const roleIcon = getRoleIcon(player.role);

  return (
    <PlayerContainer
      isAlive={player.isAlive}
      roleColor={roleColor}
      onClick={handleClick}
      onContextMenu={handleRightClick}
      sx={{
        width: size,
        height: size,
        border: player.isCheckedBySheriff ? '3px dashed #ffd700' : 'none',
      }}
    >
      <Typography
        variant="h6"
        component="div"
        sx={{
          fontWeight: 'bold',
          userSelect: 'none',
          color: (player.role === 'mafia' || player.role === 'don') ? 'white' : 'inherit'
        }}
      >
        {player.id}
      </Typography>

      {roleIcon && (
        <Box
          sx={{
            position: 'absolute',
            top: '2px',
            right: '2px',
            color: (player.role === 'mafia' || player.role === 'don') ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.7)',
            ...(player.role === 'unknown' && {
              backgroundColor: roleColor,
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            })
          }}
        >
          {roleIcon}
        </Box>
      )}
      
      {!player.isAlive && (
        <Box 
          sx={{ 
            position: 'absolute',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Box 
            sx={{ 
              position: 'absolute',
              width: '80%',
              height: '3px',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              transform: 'rotate(45deg)'
            }} 
          />
          <Box 
            sx={{ 
              position: 'absolute',
              width: '80%',
              height: '3px',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              transform: 'rotate(-45deg)'
            }} 
          />
        </Box>
      )}
      
      {/* Display votes */}
      {player.votesFor && Array.isArray(player.votesFor) && player.votesFor.length > 0 && (
        <Box 
          sx={{ 
            position: 'absolute',
            bottom: '-24px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            color: 'white',
            borderRadius: '4px',
            padding: '2px 6px',
            fontSize: '13px',
            fontWeight: 'bold',
            minWidth: '20px',
            textAlign: 'center',
            whiteSpace: 'nowrap',
            zIndex: 5,
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}
        >
          ➔ {player.votesFor.join(', ')}
        </Box>
      )}
    </PlayerContainer>
  );
};

export default PlayerCircle;